import {
  Button,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import { DragIndicator, MoreHoriz } from '@material-ui/icons';
import CreateIcon from '@material-ui/icons/Create';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_CMS_TVCHANNELS,
} from '../../../../../constants/RoleConsts';
import {
  TVCHANNEL_BOUQUETS_CREATE,
  TVCHANNEL_BOUQUETS_EDIT,
  TVCHANNEL_BOUQUETS_VIEW,
} from '../../../../../constants/TvChannelsConst';
import DeletePopupModel from '../../../../../shared/delete-popup/Delete-Popup';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';
import SearchBar from '../../../../../shared/search-bar/Search-Bar';
import TablePaginationActions from '../../../../../shared/table-pagination/TablePaginationActions';
import { deleteBouquetDetail, uploadBulkTvBouquestsByCSV } from '../../../../../utils/cmsUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import {
  bouquetSelector,
  channelNumberSelector,
  fetchAllBouquets,
  fetchChannelNumberList,
  setBouquetsSuccess,
} from '../../../cmsSlice';
import WarningPopup from '../../../../../shared/warning-popup/Warning-Popup';
import useOutsideAlerter from '../../../../../custom-hooks/useOutsideAlerter';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { handleOnDragEnd } from '../../../../../shared/custom-dragdrop/dragDrop';
import { createDataSortMapping } from '../../../../../utils/PropertyAPIUtils';
import { Dialog } from '@mui/material';
import CsvUploader from '../../../../../shared/csv-uploader/Csv-Uploader';
import {
  allFloorsSelector,
  allLocationsSelector,
  allRoomsSelector,
} from '../../../../property/propertySlice';
import { validateAlphanumeric } from '../../../../../utils/formUtils';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { DOWNLOAD_ASSETS_URL } from '../../../../../env/Configurations';

const BouquetsList = (props) => {
  const {
    onDrawerToggle,
    onViewToggle,
    selectedHotel,
    permissions,
    page,
    rowsPerPage,
    onsetRowsPerPage,
    onsetPage,
  } = props;
  const bouquetList = useSelector(bouquetSelector);
  const [bouquet, setBouquet] = useState([]);
  const [loading, setLoading] = useState(true);
  const channelList = useSelector(channelNumberSelector);
  const [channelListNum, setChannelListNum] = useState(null);
  const dispatch = useDispatch();
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
    bouquet: null,
    warn: false,
  });
  const wrapperRef = useRef(null);
  const prevHotelIdRef = useRef(selectedHotel);
  const [popUp, setPopUp] = useState(false);
  const [visible, setVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dragDisabled = !!searchText || bouquet?.length <= 1;
  const [uploadState, setUploadState] = useState(false);
  const [csvUploadStatus, setCsvUploadStatus] = useState(false);
  const [csvUploadErrorMsg, setCsvUploadErrorMsg] = useState(null);
  const [csvValidateError, setCsvValidateError] = useState(false);
  const [dataExistError, setDataExistError] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const locations = useSelector(allLocationsSelector);
  const floors = useSelector(allFloorsSelector);
  const rooms = useSelector(allRoomsSelector);

  useEffect(() => {
    setBouquet(bouquetList);
    setLoading(false);
    setSearchText('');
  }, [bouquetList]);

  useEffect(() => {
    setChannelListNum(channelList);
  }, [channelList]);

  useEffect(() => {
    if (loading || bouquet?.length === 0) return;
    const totalItems = bouquet?.length;
    const maxPageIndex = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (page > maxPageIndex) {
      onsetPage(maxPageIndex);
    }
  }, [bouquet?.length, rowsPerPage, page, onsetPage]);

  useEffect(() => {
    if (prevHotelIdRef?.current !== selectedHotel) {
      onsetPage(0);
    }
    prevHotelIdRef.current = selectedHotel;
  }, [selectedHotel]);

  const handleSearchText = async (searchText) => {
    setSearchText(searchText);
    const filteredRows = await bouquetList.filter((item) => {
      return (
        item.name.toLowerCase().includes(searchText.toLowerCase()) ||
        item.code.toLowerCase().includes(searchText.toLowerCase())
      );
    });
    onsetPage(0);
    setBouquet(filteredRows);
  };

  const handleCellMenuClick = (event, bouquet) => {
    event.stopPropagation();
    setSelectedValue({
      id: bouquet.id,
      name: bouquet.name,
      title: '',
      description: '',
      status: false,
      anchorEl: event.currentTarget,
      bouquet: bouquet,
      warn: false,
    });
  };

  const onClickView = (e) => {
    e.stopPropagation();
    if (selectedValue && selectedValue.bouquet) {
      onViewToggle(TVCHANNEL_BOUQUETS_VIEW, true, selectedValue.bouquet);
    }
  };

  const onOpenDrawer = (event, type) => {
    event.stopPropagation();
    onDrawerToggle(type, true, selectedValue.bouquet);
    handleClose();
  };

  const onViewDetails = (bouquetClicked, event) => {
    event.stopPropagation();
    if (bouquetClicked) {
      if (event?.target?.tagName === 'TD') {
        onViewToggle(TVCHANNEL_BOUQUETS_VIEW, true, bouquetClicked);
      }
    }
  };

  const deleteBouquet = async (e, channelNumber, bouquetName) => {
    e.stopPropagation();
    if (channelNumber === 0 || channelNumber === '0') {
      setSelectedValue((prev) => ({
        ...prev,
        title: 'Are you sure you want to delete',
        description: 'This action cannot be reversed.',
        status: true,
        anchorEl: null,
        warn: false,
      }));
    } else {
      setSelectedValue((prev) => ({
        ...prev,
        title: 'Unable to delete ',
        description:
          'The selected bouquet cannot be deleted as it has channels/devices assigned under it',
        status: true,
        anchorEl: null,
        warn: true,
        buttonName: 'Back',
      }));
    }
  };

  const deleteBouquetAPICall = async (e) => {
    try {
      const lang = decryptLocalStorage('lang');
      const selectedLang = lang !== 'en' ? lang : '';
      if (selectedHotel && selectedValue && selectedValue.id) {
        await deleteBouquetDetail(selectedHotel, selectedValue.id, selectedLang);
        dispatch(fetchAllBouquets(selectedHotel));
        dispatch(fetchChannelNumberList(selectedHotel));

        notificationManager('success', 'Bouquet deleted successfully');
      }
      handleClose();
    } catch (e) {
      if (e.graphQLErrors) {
        notificationManager('danger', e.graphQLErrors[0].message);
      } else {
        notificationManager('danger', 'Failed to delete Bouquet');
      }
      handleClose();
    }
  };

  const onDeleteCancel = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      warn: false,
    });
  };

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event.target.value, 10));
    onsetPage(0);
  };

  const handleClose = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      bouquet: null,
      type: '',
    });
  };

  const onClickUpload = (e) => {
    setUploadState(true);
  };

  const handleUploadDialogClose = (e) => {
    dispatch(fetchAllBouquets(selectedHotel));
    setCsvUploadStatus(null);
    setUploadState(false);
  };

  const handleCsvFileData = (itemsList) => {
    const isTemplateInstructionRow = (row) => {
      const values = Object.values(row).map((v) => String(v || '').toLowerCase().trim());
      const joined = values.join(' ');
      const instructionPatterns = [
        'character limit', 'mandatory', 'alphanumeric', 'alpha-numeric',
        'enter the', 'fill in', 'e.g.', 'example:', 'max characters',
        'max char', 'required field', 'this field', 'drop down', 'dropdown',
        'select from', 'free text', 'auto populate', 'auto-populate',
      ];
      if (values.some((s) => s.startsWith('*') && instructionPatterns.some((p) => s.includes(p)))) return true;
      if (instructionPatterns.filter((p) => joined.includes(p)).length >= 2) return true;
      return false;
    };
    const filteredItemList = itemsList.filter((row) => !isTemplateInstructionRow(row));
    const errors = [];
    if (filteredItemList.length === 0) {
      errors.push('No data rows found in the uploaded file.');
      setValidationErrors(errors);
      setCsvValidateError(true);
      return;
    }
    const dbNames = new Set((bouquetList || []).map((b) => b?.name?.trim()?.toLowerCase()).filter(Boolean));
    const dbCodes = new Set((bouquetList || []).map((b) => b?.code?.trim()?.toLowerCase()).filter(Boolean));

    const nameFreq = Object.create(null);
    const codeFreq = Object.create(null);
    filteredItemList.forEach((item) => {
      const nK = (item?.bouquetName || '').trim().toLowerCase();
      const cK = (item?.bouquetCode || '').trim().toLowerCase();
      if (nK) nameFreq[nK] = (nameFreq[nK] || 0) + 1;
      if (cK) codeFreq[cK] = (codeFreq[cK] || 0) + 1;
    });

    const fileDupNames = new Set(Object.keys(nameFreq).filter(k => nameFreq[k] > 1));
    const fileDupCodes = new Set(Object.keys(codeFreq).filter(k => codeFreq[k] > 1));

    const newItems = [];
    filteredItemList.forEach((item) => {
      const rowNum = item.__rowNum || '?';
      const rowErrors = [];
      if (!item.bouquetName?.trim()) {
        rowErrors.push('"Bouquet Name" is empty');
      } else if (item.bouquetName.trim().length > 100) {
        rowErrors.push('"Bouquet Name" exceeds 100 characters');
      }
      if (!item.bouquetCode?.trim()) {
        rowErrors.push('"Bouquet Code" is empty');
      } else {
        if (item.bouquetCode.trim().length > 8) rowErrors.push('"Bouquet Code" exceeds 8 characters');
        if (!validateAlphanumeric(item.bouquetCode.trim())) rowErrors.push('"Bouquet Code" must be alphanumeric');
      }
      const getValue = (obj, keys) => {
        const foundKey = Object.keys(obj).find((k) => keys.includes(k.toLowerCase().replace(/[-_ ]/g, '')));
        return foundKey ? obj[foundKey] : undefined;
      };

      const floorStr = (getValue(item, ['floors', 'floor', 'selectfloor']) || '').toString();
      const roomStr = (getValue(item, ['rooms', 'room', 'selectroom']) || '').toString();
      const locStr = (getValue(item, ['locations', 'location']) || '').toString();

      const floorNames = floorStr.split(',').map((f) => f.trim()).filter((f) => f);
      const roomNumbers = roomStr.split(',').map((r) => r.trim()).filter((r) => r);
      const locationNames = locStr.split(',').map((l) => l.trim()).filter((l) => l);

      const invalidFloors = floorNames.filter((f) => {
        return !floors?.some((fl) =>
          fl?.name?.toLowerCase() === f.toLowerCase() ||
          String(fl?.number)?.toLowerCase() === f.toLowerCase()
        );
      });
      if (invalidFloors.length > 0) rowErrors.push(`"Floors" not found: ${invalidFloors.join(', ')}`);

      const invalidRooms = roomNumbers.filter((r) => !rooms?.some((rm) => String(rm?.number)?.toLowerCase() === r.toLowerCase()));
      if (invalidRooms.length > 0) rowErrors.push(`"Rooms" not found: ${invalidRooms.join(', ')}`);

      const invalidLocations = locationNames.filter((l) => !locations?.some((loc) => loc?.name?.toLowerCase() === l.toLowerCase()));
      if (invalidLocations.length > 0) rowErrors.push(`"Locations" not found: ${invalidLocations.join(', ')}`);

      if (invalidFloors.length === 0 && invalidRooms.length === 0 && floorNames.length > 0 && roomNumbers.length > 0) {
        const floorObjs = floors?.filter((fl) =>
          floorNames.some((fn) =>
            fl?.name?.toLowerCase() === fn.toLowerCase() ||
            String(fl?.number)?.toLowerCase() === fn.toLowerCase()
          )
        );
        
        roomNumbers.forEach((rNo) => {
          if (!/^\d+$/.test(rNo)) return;
          const rNum = parseInt(rNo, 10);
          const isBelongsToFloor = floorObjs?.some((fo) => {
            return fo?.roomRangeList?.some((range) => {
              const from = parseInt(range.rangeFrom, 10);
              const to = parseInt(range.rangeTo, 10);
              return !isNaN(from) && !isNaN(to) && rNum >= from && rNum <= to;
            });
          });

          if (!isBelongsToFloor) {
            rowErrors.push(`Room "${rNo}" does not belong to the specified floor(s)`);
          }
        });
      }

      const nK = (item?.bouquetName || '').trim().toLowerCase();
      const cK = (item?.bouquetCode || '').trim().toLowerCase();
      const nameExists = nK && (dbNames.has(nK) || fileDupNames.has(nK));
      const codeExists = cK && (dbCodes.has(cK) || fileDupCodes.has(cK));

      if (nameExists || codeExists) {
        const reasons = [];
        if (nameExists) reasons.push(`name '${(item?.bouquetName || '').trim()}'`);
        if (codeExists) reasons.push(`code '${(item?.bouquetCode || '').trim()}'`);
        rowErrors.push(`Already exists or duplicated in file (${reasons.join(' and ')})`);
      }

      if (rowErrors.length > 0) {
        errors.push(`Row ${rowNum}: ${rowErrors.join(', ')}`);
      } else {
        newItems.push(item);
      }
    });

    if (errors.length > 0) {
      setValidationErrors(errors);
      setCsvValidateError(true);
      setDataExistError(false);
      return;
    }

    if (newItems.length > 0) {
      const listToUpload = newItems.map((item) => {
        return {
          name: item.bouquetName?.trim(),
          code: item.bouquetCode?.trim(),
          floor:
            item.floors && item.floors.trim().length > 0
              ? item.floors.split(',').map((i) => i.trim()).filter((i) => i)
              : [],
          room:
            item.rooms && item.rooms.trim().length > 0 ? item.rooms.split(',').map((i) => i.trim()).filter((i) => i) : [],
          location:
            item.locations && item.locations.trim().length > 0
              ? item.locations.split(',').map((i) => i.trim()).filter((i) => i)
              : [],
          hotelId: selectedHotel,
        };
      });
      setValidationErrors([]);
      setCsvValidateError(false);
      handleCsvDataSubmit(false, listToUpload);
    } else {
      setValidationErrors(errors);
      setCsvValidateError(true);
      setDataExistError(false);
    }
  };

  const handleCsvDataSubmit = async (isReplacing, payloadLists) => {
    setCsvUploadStatus('uploading');

    try {
      const payload = {
        isReplace: isReplacing,
        hotelId: selectedHotel,
        bouquets: payloadLists,
      };
      const selectedLanguage = decryptLocalStorage('lang');
      const language = selectedLanguage !== 'en' ? selectedLanguage : '';
      const res = await uploadBulkTvBouquestsByCSV(payload, language);
      if (res?.data?.uploadBouquetsByCSV?.status === 'Success') {
        setCsvUploadStatus('success');
      } else {
        setCsvUploadStatus('error');
      }
    } catch (e) {
      setCsvUploadStatus('server-error');
    }
  };
  useOutsideAlerter(wrapperRef, visible, setPopUp);

  const generateExportData = (bouquetData) => {
    const fields = ['name', 'code', 'floor', 'room', 'location'];
    return bouquetData.map((record) =>
      Object.fromEntries(
        Object?.entries(record)?.filter(([key, value]) =>
          fields?.includes(key) ? [key, "'" + value] : null,
        ),
      ),
    );
  };

  const generateHeaders = [
    { label: 'Bouquet Name', key: 'name' },
    { label: 'Bouquet Code', key: 'code' },
    { label: 'Floors', key: 'floor' },
    { label: 'Rooms', key: 'room' },
    { label: 'Locations', key: 'location' },
  ];

  return (
    <div className='mt-4 mb-4 group-list-container'>
      <Dialog fullScreen open={uploadState} onClose={handleUploadDialogClose}>
        <CsvUploader
          onClose={handleUploadDialogClose}
          pageTitle='Bouquets Info'
          onFileReadingComplete={(e) => handleCsvFileData(e)}
          csvUploadStatus={csvUploadStatus}
          validFormatError={csvValidateError}
          setValidFormatError={setCsvValidateError}
          dataExistError={dataExistError}
          setDataExistError={setDataExistError}
          listName='Bouquets'
          sectionTItle='TV Channels'
          csvUploadErrorMsg={csvUploadErrorMsg}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
          downloadLink={DOWNLOAD_ASSETS_URL + '/cmsAssets/TV_Channel_Bouquet_Template.xlsx'}
        />
      </Dialog>
      {selectedValue.id && selectedValue.status === true && !selectedValue.warn && (
        <DeletePopupModel
          deletePopUp={selectedValue}
          onDelete={deleteBouquetAPICall}
          onDeleteCancel={onDeleteCancel}
        />
      )}
      {selectedValue.id && selectedValue.status === true && selectedValue.warn && (
        <WarningPopup warningPopup={selectedValue} onWarningClose={handleClose} warn={true} />
      )}
      <div className='row mx-2 d-flex'>
        <SearchBar
          type={TVCHANNEL_BOUQUETS_CREATE}
          buttonName='Add Bouquet'
          onOpenDrawer={onOpenDrawer}
          displayAddOrEdit={
            permissions[RBAC_TYPE_CMS_TVCHANNELS] === RBAC_PERMISSION_LEVEL_FULL_ACCESS
              ? 'show'
              : 'hide'
          }
          setSearchText={handleSearchText}
          visible={visible}
          setVisible={setVisible}
          popUp={popUp}
          setPopUp={setPopUp}
          placeHolder={bouquet}
          setPlaceHolder={setBouquet}
          globalPlaceHolder={bouquetList}
          setGlobalPlaceHolder={setBouquetsSuccess}
          dispatch={dispatch}
          mappingApi={createDataSortMapping}
          displayText='TV channels bouquet'
          module='BOUQUET'
          searchText={searchText}
          setSearch={setSearchText}
          extraSecondaryBtnDisplay={true}
          extraSecondaryBtnText='Upload'
          onClickSecondaryBtn={onClickUpload}
          exportBtn
          exportBtnTitle='Export'
          exportData={generateExportData(bouquet)}
          actionMenuItems={[]}
          headers={generateHeaders}
        />
      </div>
      <div ref={wrapperRef} className='row mx-2 d-flex mt-4'>
        {loading ? (
          <div className='pt-4 col-md-12'>
            <LinearProgressBar />
          </div>
        ) : (
          bouquet && (
            <TableContainer>
              <Table className='list-table' aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell className='w-40'>Name</TableCell>
                    <TableCell className='w-50'>Code</TableCell>
                    <TableCell className='w-50'>No of Channels</TableCell>
                    <TableCell align='right'></TableCell>
                  </TableRow>
                </TableHead>

                <DragDropContext
                  onDragEnd={(event) =>
                    handleOnDragEnd(
                      event,
                      setVisible,
                      bouquet,
                      setBouquet,
                      false,
                      page,
                      rowsPerPage,
                    )
                  }
                  isDropDisabled={dragDisabled}
                >
                  <Droppable droppableId='cms' isDropDisabled={dragDisabled}>
                    {(provided) => (
                      <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                        {(rowsPerPage > 0
                          ? bouquet.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          : bouquet
                        ).map((bouquetItem, i) => (
                          <Draggable
                            key={bouquetItem.id}
                            draggableId={bouquetItem.id}
                            index={i}
                            isDragDisabled={dragDisabled}
                          >
                            {(provided) => (
                              <TableRow
                                key={i}
                                className='border-white pointer'
                                onClick={(e) => {
                                  onViewDetails(bouquetItem, e);
                                }}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <TableCell>
                                  {!dragDisabled && (
                                    <DragIndicator className='res-menu-accordion-drag' />
                                  )}
                                </TableCell>
                                <TableCell className='w-40'>{bouquetItem?.name}</TableCell>
                                <TableCell className='w-50'>{bouquetItem?.code}</TableCell>
                                <TableCell className='w-50'>
                                  {!channelListNum[bouquetItem?.id]
                                    ? '0'
                                    : channelListNum[bouquetItem?.id]}
                                </TableCell>
                                <TableCell align='right'>
                                  {permissions[RBAC_TYPE_CMS_TVCHANNELS] ===
                                    RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
                                    <Button
                                      aria-controls='simple-menu'
                                      aria-haspopup='true'
                                      onClick={(e) => {
                                        handleCellMenuClick(e, bouquetItem);
                                      }}
                                      className='showpopupInner'
                                    >
                                      <MoreHoriz className='showpopupInner' />
                                    </Button>
                                  )}
                                  <Menu
                                    id={i}
                                    anchorEl={selectedValue?.anchorEl}
                                    disableRestoreFocus
                                    MenuListProps={{ autoFocusItem: false }}
                                    keepMounted
                                    open={
                                      Boolean(selectedValue.anchorEl) &&
                                      selectedValue.id === bouquetItem.id
                                    }
                                    onClose={handleClose}
                                    className='shadow'
                                  >
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        onOpenDrawer(e, TVCHANNEL_BOUQUETS_EDIT);
                                      }}
                                    >
                                      <CreateIcon className='me-2 action-menu-icon' />
                                      Edit
                                    </MenuItem>
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        onClickView(e);
                                      }}
                                    >
                                      <VisibilityIcon className='me-2 action-menu-icon' />
                                      View
                                    </MenuItem>
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        deleteBouquet(
                                          e,
                                          !channelListNum[bouquetItem.id]
                                            ? '0'
                                            : channelListNum[bouquetItem.id],
                                          bouquetItem.name,
                                        );
                                      }}
                                    >
                                      <DeleteForeverIcon className='me-2 action-menu-icon' />
                                      Delete
                                    </MenuItem>
                                  </Menu>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </DragDropContext>
                <TableFooter>
                  <TableRow className='paginator'>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                      count={bouquet.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      SelectProps={{
                        inputProps: { 'aria-label': 'rows per page' },
                        native: true,
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )
        )}
      </div>
    </div>
  );
};

export default BouquetsList;
